import type { ComponentType } from 'react';
import { createContext } from 'react';

import type { useFlag } from '@fusion/feature-flags';

import type { AppConfigErrorScreen } from '../features/app-config/app-config-error-screen/cdm-app-config-error-screen';
import type { useLocations } from '../features/app-config/hooks/cdm-use-locations';
import type { useFeatureToggleConfig } from '../features/app-config/hooks/use-query/cdm-use-feature-toggle-config';
import type { useSelectedRootLocationConfiguration } from '../features/app-config/hooks/use-selected-root-location-configuration';
import type { useIsSchedulerEnabled } from '../features/checklist/checklist-scheduler/hooks/use-is-scheduler-enabled';
import type { useFeatureObservationsOnIdm } from '../hooks/feature-flags/use-feature-observations-on-idm';
import type { useIsDesktop } from '../hooks/useIsDesktop';
import type { useCurrentUserContext } from '../providers/current-user-provider/current-user-provider';

import type { MobileVersionPageType } from './cdm-page-layout';
import type { useHasUserEnabledAtlasHome } from './landing-page/hooks/use-has-user-enabled-atlas-home';

export interface CdmRoutesContextInterface {
  useIsDesktop: typeof useIsDesktop;
  useFeatureObservationsOnIdm: typeof useFeatureObservationsOnIdm;
  useSelectedRootLocationConfiguration: typeof useSelectedRootLocationConfiguration;
  useCurrentUserContext: typeof useCurrentUserContext;
  useLocations: typeof useLocations;
  useFeatureToggleConfig: typeof useFeatureToggleConfig;
  useIsSchedulerEnabled: typeof useIsSchedulerEnabled;
  useFlag: typeof useFlag;
  useHasUserEnabledAtlasHome: typeof useHasUserEnabledAtlasHome;
  AppConfigErrorScreen: typeof AppConfigErrorScreen;
  PageLayout: ComponentType<{ page?: MobileVersionPageType }>;
  LandingPage: ComponentType;
  UserProfilePage: ComponentType;
  ActivityDesktop: ComponentType;
  ActivityListView: ComponentType;
  ActivityPlanningView: ComponentType;
  ActivityDetailsView: ComponentType;
  TemplatesMobile: ComponentType;
  TemplatesOverviewNew: ComponentType;
  TemplateCreatedConfirmationPage: ComponentType;
  SchedulesPage: ComponentType;
  AssetDetails: ComponentType;
  Asset3d: ComponentType;
  AssetTrends: ComponentType;
  AssetProperties: ComponentType;
  AssetActivities: ComponentType;
  AssetNotifications: ComponentType;
  NotificationDetailsView: ComponentType;
  AssetDocuments: ComponentType;
  ActivityChecklistMobile: ComponentType;
  IdmObservationsDesktopTablePage: ComponentType;
  IdmObservationsDesktopFormPage: ComponentType;
  IdmObservationsMobileListPage: ComponentType;
  IdmObservationMobileOverviewPage: ComponentType;
  PageNotFound: ComponentType;
  DataModelingOverridePage: ComponentType;
  ChecklistItemObservationFormPage: ComponentType;
  ChecklistItemObservationListPage: ComponentType;
}

export const CdmRoutesContext = createContext<CdmRoutesContextInterface | null>(
  null
);
